import * as React from 'react'
import type {
  OutputColumn,
  PageTable,
  TabularConfigExamplePreview
} from '@axilon-apps/domains-ontology-ui-state'
import { Card } from '../../../components/ui/card'
import { WriteButton } from '../../IngestPage/shared/WriteButton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../../components/ui/table'
import { cn } from '../../../lib/utils'
import { CHIP, COMPACT_TABLE, MONO_WRAP, SECTION_LABEL } from '../../IngestPage/shared/compact'
import { changedSections } from '../util/draftChanges'
import type { ConfigSubject } from '../util/subject'
import { ChangeNote } from './ChangeNote'

type PreviewTable = TabularConfigExamplePreview['tables'][number]

/** What the tab shows: the contract itself, or what one example produced under it. */
type TablesView = { kind: 'schema' } | { kind: 'example'; name: string }

/**
 * The output contract. A pill row picks the view: Schema (one card per table
 * with its columns) or — when the subject has previews (a draft does; a saved
 * version shows the schema only) — one example, whose cards hold every row
 * the builder kept from it, as a real grid. An agent parser with page tables
 * shows them above the output tables in the schema view.
 */
export function TablesTab({
  subject,
  canManage,
  onEdit
}: {
  subject: ConfigSubject
  canManage?: boolean
  onEdit?: () => void
}) {
  const examples = subject.previews.map((preview) => preview.exampleName)
  const [selected, setSelected] = React.useState<TablesView>({ kind: 'schema' })
  const view: TablesView =
    selected.kind === 'example' && examples.includes(selected.name) ? selected : { kind: 'schema' }
  const preview =
    view.kind === 'example'
      ? subject.previews.find((candidate) => candidate.exampleName === view.name)
      : undefined
  const sections = changedSections(subject.diff)
  const changed = sections.has('output')
  const pageTablesChanged = sections.has('pageTables')
  const pageTables = subject.pageTables?.length ? subject.pageTables : undefined
  const finalizeEntrypoint =
    subject.agentScripts?.scripts.find((script) => script.role === 'finalize')?.entrypoint ??
    'the finalize script'
  const descriptionHeader = subject.parserKind === 'agent' ? 'Description (prompt)' : 'Description'
  const pill = (pressed: boolean) =>
    cn(
      CHIP,
      'cursor-pointer transition-colors',
      pressed ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted'
    )

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {subject.compareLabel ? (
        <ChangeNote
          section="Output contract"
          changed={changed}
          compareLabel={subject.compareLabel}
          changedText="the draft's version is shown"
        />
      ) : null}
      {subject.compareLabel && (pageTables || pageTablesChanged) ? (
        <ChangeNote
          section="Page tables"
          changed={pageTablesChanged}
          compareLabel={subject.compareLabel}
          changedText={pageTables ? "the draft's version is shown" : 'none now'}
        />
      ) : null}
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-1.5 text-11" role="group" aria-label="View">
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => setSelected({ kind: 'schema' })}
              aria-pressed={view.kind === 'schema'}
              className={pill(view.kind === 'schema')}
              data-testid="tables-view-schema"
            >
              Schema
            </button>
          </div>
          {examples.length ? (
            <div
              className="flex flex-wrap items-center gap-1.5"
              role="group"
              aria-label="Examples"
              data-testid="tables-examples"
            >
              <span className="text-ink-3">Examples</span>
              {examples.map((name) => {
                const pressed = view.kind === 'example' && view.name === name
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setSelected({ kind: 'example', name })}
                    aria-pressed={pressed}
                    className={cn(pill(pressed), 'font-mono')}
                  >
                    {name}
                    {name === subject.heldExampleName ? (
                      <span title="the file that started this rebuild">●</span>
                    ) : null}
                  </button>
                )
              })}
            </div>
          ) : subject.exampleNames.length ? (
            <p className="text-ink-3" data-testid="tables-examples">
              Examples{' '}
              <span className="font-mono text-ink-2">{subject.exampleNames.join(', ')}</span> ·
              extracted rows are kept with the build that made this version, not the saved config
            </p>
          ) : null}
        </div>
        {onEdit ? (
          <WriteButton canManage={canManage === true} size="sm" variant="outline" onClick={onEdit}>
            Edit contract
          </WriteButton>
        ) : null}
      </div>
      {pageTables && view.kind === 'schema' ? (
        <>
          <PageTables
            pageTables={pageTables}
            finalizeEntrypoint={finalizeEntrypoint}
            descriptionHeader={descriptionHeader}
          />
          <span className={SECTION_LABEL}>Output tables</span>
        </>
      ) : null}
      {pageTables && view.kind === 'example' ? (
        <p className="text-11 text-ink-3" data-testid="finalize-rows-note">
          These are the rows <span className="font-mono">{finalizeEntrypoint}</span> wrote from{' '}
          {view.name}'s page readings; each page's own reading is on the Pages tab.
        </p>
      ) : null}
      {subject.output.tables.map((table) => {
        const rows = preview?.tables.find((candidate) => candidate.table === table.table)
        return (
          <Card
            key={table.table}
            className="min-w-0 gap-0 overflow-hidden p-0"
            data-testid={`table-card-${table.table}`}
          >
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-line px-3 py-2">
              <h3 className="font-mono text-xs font-semibold">{table.table}</h3>
              <span className="text-11 text-ink-3">
                {table.mode} · key {table.key.length ? table.key.join(', ') : '—'}
                {table.ingestOncePerAsOfDate ? ' · once per as-of date' : ''}
                {' · '}
                {table.columns.length} column{table.columns.length === 1 ? '' : 's'}
              </span>
            </div>

            {view.kind === 'schema' ? (
              <ColumnsTable
                label={`${table.table} schema`}
                columns={table.columns}
                keyColumns={table.key}
                descriptionHeader={descriptionHeader}
              />
            ) : rows ? (
              <ExtractedRows rows={rows} example={view.name} />
            ) : (
              <p
                className="px-3 py-3 text-xs text-ink-3"
                data-testid={`extracted-rows-${table.table}`}
              >
                The script produced no rows for this table from {view.name}.
              </p>
            )}
          </Card>
        )
      })}
    </div>
  )
}

/**
 * What the vision call reads off each page when a finalize script writes the
 * output tables. The parser's working shape, not the contract, so it is
 * read-only here.
 */
function PageTables({
  pageTables,
  finalizeEntrypoint,
  descriptionHeader
}: {
  pageTables: PageTable[]
  finalizeEntrypoint: string
  descriptionHeader: string
}) {
  return (
    <section
      className="flex min-w-0 flex-col gap-2"
      aria-label="Read from each page"
      data-testid="page-tables"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className={SECTION_LABEL}>Read from each page</span>
        <span className="text-11 text-ink-3">
          The vision call fills these; <span className="font-mono">{finalizeEntrypoint}</span> turns
          them into the output tables below.
        </span>
      </div>
      {pageTables.map((table) => (
        <Card
          key={table.table}
          className="min-w-0 gap-0 overflow-hidden p-0"
          data-testid={`page-table-card-${table.table}`}
        >
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-line px-3 py-2">
            <h3 className="font-mono text-xs font-semibold">{table.table}</h3>
            <span className="text-11 text-ink-3">
              {table.columns.length} column{table.columns.length === 1 ? '' : 's'}
            </span>
          </div>
          <ColumnsTable
            label={`${table.table} page table`}
            columns={table.columns}
            descriptionHeader={descriptionHeader}
          />
        </Card>
      ))}
    </section>
  )
}

function ColumnsTable({
  label,
  columns,
  keyColumns = [],
  descriptionHeader
}: {
  label: string
  columns: readonly OutputColumn[]
  keyColumns?: readonly string[]
  descriptionHeader: string
}) {
  return (
    <section className="flex flex-col" aria-label={label}>
      <Table className={COMPACT_TABLE}>
        <TableHeader>
          <TableRow>
            <TableHead>Column</TableHead>
            <TableHead>Type</TableHead>
            <TableHead className="text-center">Req</TableHead>
            <TableHead className="w-1/2">{descriptionHeader}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {columns.map((column) => (
            <TableRow key={column.name}>
              <TableCell className={cn(MONO_WRAP, 'font-medium')}>
                {column.name}
                {keyColumns.includes(column.name) ? (
                  <span className="ml-1.5 font-sans text-10 font-normal text-ink-3">key</span>
                ) : null}
              </TableCell>
              <TableCell className="font-mono text-11 text-ink-3">{column.type}</TableCell>
              <TableCell className="text-center text-ink-3">{column.required ? '✓' : ''}</TableCell>
              <TableCell className="text-ink-2">{column.description ?? ''}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  )
}

/**
 * Every row the builder kept from one example (it stores the first 20 of
 * each table), as the script produced them: the preview's own column order,
 * values only. Wide tables scroll inside the grid, not the page.
 */
function ExtractedRows({ rows, example }: { rows: PreviewTable; example: string }) {
  const kept = rows.rows.length
  const summary = rows.rowsTruncated
    ? `first ${kept} of ${rows.rowCount} rows`
    : `${kept} row${kept === 1 ? '' : 's'}`
  return (
    <section
      className="flex min-w-0 flex-col"
      aria-label={`${rows.table} rows extracted from ${example}`}
      data-testid={`extracted-rows-${rows.table}`}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 px-3 pt-2.5 pb-1">
        <p className={SECTION_LABEL}>Extracted from {example}</p>
        <span className="text-11 text-ink-3">
          {summary}
          {rows.cellsTruncated ? ' · long values shortened' : ''}
        </span>
      </div>
      {kept === 0 ? (
        <p className="px-3 pb-3 text-xs text-ink-3">The script produced no rows for this table.</p>
      ) : (
        <div className="min-w-0 overflow-x-auto">
          <table className="w-full text-left text-11">
            <thead className="bg-bg-sunk/60">
              <tr className="border-y border-line">
                <th className="w-8 px-3 py-1.5 text-right font-medium text-ink-3 select-none">#</th>
                {rows.columns.map((column) => (
                  <th
                    key={column}
                    className="px-3 py-1.5 font-mono font-medium whitespace-nowrap text-ink"
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b border-line/60 last:border-0">
                  <td className="px-3 py-1 text-right text-ink-3 tabular-nums select-none">
                    {rowIndex + 1}
                  </td>
                  {rows.columns.map((_, cellIndex) => {
                    const value = row[cellIndex] ?? ''
                    return (
                      <td
                        key={cellIndex}
                        className={cn(
                          'max-w-56 truncate px-3 py-1 font-mono whitespace-nowrap',
                          value === '' && 'text-ink-4'
                        )}
                        title={value}
                      >
                        {value === '' ? '∅' : value}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
